import React, { useCallback, useEffect, useRef, useState } from 'react';
import logo from '../assets/chanakyax-logo.png';
import { installMaintenanceFetchHook } from '../utils/maintenanceFetchHook';
import { forceFreshLogin } from '../utils/sessionExpiry';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const POLL_IDLE_MS = 20000;
const POLL_ACTIVE_MS = 10000;
const BANNER_LEAD_MS = 30 * 60 * 1000;

const fmtTime = (ms) => new Date(ms).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
const fmtDayTime = (ms) => {
  const d = new Date(ms);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay ? fmtTime(ms) : `${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}, ${fmtTime(ms)}`;
};
const fmtLeft = (ms) => {
  const m = Math.ceil(ms / 60000);
  if (m < 1) return 'less than a minute';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
};

// Sits above every route. Polls the public /api/platform/status endpoint and
//  - 30 min before a scheduled window: shows a dismissible heads-up banner,
//  - while maintenance is active: covers the app with a "we'll be right back" screen,
//  - when it ends: signs everyone out and reloads, so they sign in fresh on the freshly deployed version.
// The Super Admin panel is never covered (it's how maintenance gets turned off).
export default function MaintenanceGate({ children }) {
  const exempt = window.location.pathname.startsWith('/superadmin');
  const [status, setStatus] = useState({ state: 'off' });
  const [, setTick] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const statusRef = useRef(status);
  const offsetRef = useRef(0); // server clock minus this device's clock
  const prevStateRef = useRef('off');
  const lastKickRef = useRef(0);
  const pollTimerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/api/platform/status`, { cache: 'no-store' });
      if (!res.ok) return; // backend restarting etc. — keep showing whatever we last knew
      const data = await res.json();
      offsetRef.current = new Date(data.server_time).getTime() - Date.now();
      const next = data.maintenance || { state: 'off' };
      if (prevStateRef.current === 'active' && next.state !== 'active') {
        forceFreshLogin();
        return;
      }
      prevStateRef.current = next.state;
      statusRef.current = next;
      setStatus(next);
    } catch (_) { /* unreachable — keep the last known state */ }
  }, []);

  // Any API call answered with 503 + code MAINTENANCE refreshes the status straight away,
  // so the screen appears the moment maintenance starts instead of at the next poll.
  useEffect(() => {
    if (exempt) return undefined;
    return installMaintenanceFetchHook(BASE, load);
  }, [exempt, load]);

  useEffect(() => {
    if (exempt) return undefined;
    let stopped = false;
    const loop = async () => {
      await load();
      if (!stopped) pollTimerRef.current = setTimeout(loop, prevStateRef.current === 'active' ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    };
    loop();
    const wake = () => { if (document.visibilityState !== 'hidden') { clearTimeout(pollTimerRef.current); loop(); } };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    window.addEventListener('online', wake);
    return () => {
      stopped = true;
      clearTimeout(pollTimerRef.current);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
      window.removeEventListener('online', wake);
    };
  }, [exempt, load]);

  // 1-second clock for countdowns; also nudges a status refresh the moment a window opens/closes.
  useEffect(() => {
    if (exempt || status.state === 'off') return undefined;
    const iv = setInterval(() => {
      setTick(n => n + 1);
      const s = statusRef.current;
      const now = Date.now() + offsetRef.current;
      const due = (s.state === 'upcoming' && s.starts_at && now >= new Date(s.starts_at).getTime())
        || (s.state === 'active' && s.ends_at && now >= new Date(s.ends_at).getTime());
      if (due && Date.now() - lastKickRef.current > 4000) { lastKickRef.current = Date.now(); load(); }
    }, 1000);
    return () => clearInterval(iv);
  }, [exempt, status.state, load]);

  useEffect(() => {
    try { setDismissed(sessionStorage.getItem(`cx-maint-banner-${status.starts_at}`) === '1'); } catch (_) { setDismissed(false); }
  }, [status.starts_at]);

  if (exempt) return children;

  const now = Date.now() + offsetRef.current;
  const startsAt = status.starts_at ? new Date(status.starts_at).getTime() : null;
  const endsAt = status.ends_at ? new Date(status.ends_at).getTime() : null;
  const showBanner = status.state === 'upcoming' && startsAt && startsAt - now <= BANNER_LEAD_MS && !dismissed;

  const dismissBanner = () => {
    try { sessionStorage.setItem(`cx-maint-banner-${status.starts_at}`, '1'); } catch (_) {}
    setDismissed(true);
  };

  return (
    <>
      {children}

      {showBanner && (
        <div role="status" style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 19000,
          background: 'linear-gradient(90deg,#b45309,#d97706)', color: '#fff',
          padding: '9px 44px 9px 16px', fontSize: 13, fontWeight: 600, textAlign: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.25)', lineHeight: 1.4,
        }}>
          🔧 Scheduled maintenance {fmtDayTime(startsAt)}{endsAt ? ` – ${fmtTime(endsAt)}` : ''}
          {startsAt - now > 0 ? ` (starts in ${fmtLeft(startsAt - now)})` : ' — starting now'}.
          {' '}Please save your work.{status.message ? ` ${status.message}` : ''}
          <button onClick={dismissBanner} aria-label="Dismiss" style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', borderRadius: 6,
            width: 26, height: 26, cursor: 'pointer', fontSize: 15, lineHeight: 1,
          }}>×</button>
        </div>
      )}

      {status.state === 'active' && (
        <div role="alertdialog" aria-label="Server in maintenance" style={{
          position: 'fixed', inset: 0, zIndex: 20000, overflowY: 'auto',
          background: 'linear-gradient(135deg,#060d24 0%,#0f1f45 55%,#1a3a6b 100%)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{ maxWidth: 520, width: '100%', textAlign: 'center' }}>
            <img src={logo} alt="ChanakyaX" style={{ display: 'block', width: 240, maxWidth: '70%', height: 'auto', margin: '0 auto 28px' }} />
            <div style={{ fontSize: 44, marginBottom: 6 }} aria-hidden="true">🔧</div>
            <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 12px' }}>Server in Maintenance</h1>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: 'rgba(255,255,255,0.78)', margin: '0 0 18px' }}>
              {status.message || "We're making ChanakyaX better. Your data is safe — we'll be back online very shortly."}
            </p>
            <div style={{
              display: 'inline-block', padding: '12px 22px', borderRadius: 12, marginBottom: 22,
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', fontSize: 14,
            }}>
              {endsAt ? (
                endsAt - now > 0
                  ? <>Back by <b>{fmtDayTime(endsAt)}</b> · about <b>{fmtLeft(endsAt - now)}</b> to go</>
                  : <>Wrapping up… checking again in a moment</>
              ) : <>We'll be back shortly</>}
            </div>
            <div>
              <button onClick={load} style={{
                padding: '11px 28px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: '#FC5C0B', color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
              }}>Check again</button>
            </div>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 22 }}>
              This page refreshes by itself when we're back.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
